import { Component, inject, OnInit, CUSTOM_ELEMENTS_SCHEMA, OnDestroy } from '@angular/core';
import { IonMenu, MenuController, NavController, ModalController } from '@ionic/angular/standalone';
import { User } from 'src/app/core/models/User';
import { Subscription, race, timer } from 'rxjs';
import { filter, take, map } from 'rxjs/operators';

import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonText,
  IonNote,
  IonSkeletonText,
} from '@ionic/angular/standalone';

import { AuthService } from 'src/app/core/services/auth/auth.service';
import { UserRolesService } from 'src/app/core/services/auth/user-roles.service';
import { CharityFollowService } from 'src/app/core/services/charity/charity-follow.service';
import { CommonModule } from '@angular/common';
import { AvatarComponent } from '../avatar/avatar.component';
import { CharityService } from 'src/app/core/services/charity/charity.service';
import { BusinessService } from 'src/app/core/services/business/business.service';
import { TabService } from 'src/app/core/services/navigation/tab.service';
import { StateType } from 'src/app/core/models/StateType';
import { Charity } from 'src/app/core/models/Charity';
import { Business } from 'src/app/core/models/Business';
import { environment } from 'src/environments/environment';
import { CharitySelectorBottomSheetComponent } from '../charity-selector-bottom-sheet/charity-selector-bottom-sheet.component';
import { BusinessSelectorBottomSheetComponent } from 'src/app/role-business/components/business-selector-bottom-sheet/business-selector-bottom-sheet.component';
import { CreditsService } from 'src/app/core/services/credits/credits.service';

@Component({
  selector: 'app-menu',
  templateUrl: './menu-user.component.html',
  styleUrls: ['./menu-user.component.scss'],
  standalone: true,
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButton,
    IonContent,
    IonList,
    IonItem,
    IonLabel,
    IonText,
    IonNote,
    IonSkeletonText,
    CommonModule,
    AvatarComponent,
    IonMenu
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MenuUserComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();
  private menuController = inject(MenuController);
  private modalController = inject(ModalController);
  private userRolesService = inject(UserRolesService);

  loggedInUser!: User;
  authorityLoaded: boolean = false;
  permittedCharities: Charity[] = [];
  permittedBusinesses: Business[] = [];
  following: string = "0";
  donations: string = "0";
  credits: string = "$0.00";
  authService: AuthService = inject(AuthService);
  avatarUrl: string = 'assets/ic-profile-avatar.svg';
  isSwitchingToCharity: boolean = false;
  isSwitchingToBusiness: boolean = false;
  
  appVersion: string = environment.appVersion || '';
  shouldShowVersion: boolean = true;

  constructor(
    private navController: NavController,
    private charityService: CharityService,
    private businessService: BusinessService,
    private tabService: TabService,
    private charityFollowService: CharityFollowService,
    private creditsService: CreditsService,
  ) {}

  ngOnInit() {
    this.subscribeToUserInfo();
    this.subscribeToRoles();
  }

  private subscribeToUserInfo(): void {
    this.subscriptions.add(
      this.authService.user$.subscribe((user: User) => {
        if (user) {
          this.loggedInUser = user;
          this.avatarUrl = user.avatarUrl ?? '';
          this.initializeFollowingCount();
          this.initializeCreditBalance();

          const donations: string | null = sessionStorage.getItem('donationCount');
          if (donations) this.donations = donations;
        }
      }),
    );

    this.subscriptions.add(
      this.charityFollowService.followingCount$.subscribe((count: number) => {
        this.following = count.toString();
      })
    );
  }

  private subscribeToRoles(): void {
    this.subscriptions.add(
      this.userRolesService.rolesLoaded$.subscribe((loaded: boolean) => {
        this.authorityLoaded = loaded;
      })
    );
    this.subscriptions.add(
      this.userRolesService.permittedCharities$.subscribe((charities: Charity[]) => {
        this.permittedCharities = charities;
      })
    );
    this.subscriptions.add(
      this.userRolesService.permittedBusinesses$.subscribe((businesses: Business[]) => {
        this.permittedBusinesses = businesses;
      })
    );
  }

  private initializeFollowingCount(): void {
    if (this.loggedInUser?.id) {
      this.subscriptions.add(
        this.charityFollowService.getFollowedCharitiesList(this.loggedInUser.id).subscribe({
          next: (followedCharities) => {
            this.following = followedCharities.length.toString();
          },
          error: (error) => {
            console.error('Error initializing following count:', error);
          }
        })
      );
    }
  }

  private initializeCreditBalance(): void {
    if (this.loggedInUser?.id) {
      this.subscriptions.add(
        this.creditsService.getUserBalance(this.loggedInUser.id).subscribe({
          next: (userCredits) => {
            const dollars: number = userCredits.credits / 100;
            this.credits = dollars.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
          },
          error: (error) => {
            console.error('Error initializing credit balance:', error);
          }
        })
      );
    }
  }

  async signOut(): Promise<void> {
    await this.authService.logout();
    this.menuController.close('app-menu');
    this.navController.navigateRoot('/welcome');
  }

  goToItem(item: string): void {
    this.menuController.close('app-menu');
    this.navController
      .navigateForward(['/main/' + item + '/' + this.loggedInUser?.id]);
  }

  goToPage(page: string): void {
    this.menuController.close('app-menu');
    this.navController.navigateForward(['/main/' + page]);
  }

  goToMyProfile(): void {
    if (!this.loggedInUser?.id) {
      return;
    }
    this.menuController.close('app-menu');
    this.navController.navigateForward(['/main/user', this.loggedInUser.id]);
  }

  async switchToBusiness(): Promise<void> {
    if (this.isSwitchingToBusiness) {
      return;
    }

    if (!this.permittedBusinesses || this.permittedBusinesses.length === 0) {
      return;
    }
    
    // If multiple businesses, show selector modal
    if (this.permittedBusinesses.length > 1) {
      const modal: HTMLIonModalElement = await this.modalController.create({
        component: BusinessSelectorBottomSheetComponent,
        cssClass: 'bottom-sheet-modal',
        componentProps: { 
          permittedBusinesses: this.permittedBusinesses,
          currentBusinessId: null
        },
        backdropDismiss: true,
      });
      
      modal.onDidDismiss().then((result) => {
        if (result.data?.selected) {
          this.menuController.close('app-menu');
        } 
      });
      
      await modal.present();
      return;
    }
    
    await this.performBusinessSwitch();
  }

  private async performBusinessSwitch(): Promise<void> {
    this.isSwitchingToBusiness = true;
    const targetBusinessId: string = String(this.permittedBusinesses[0].id!);

    this.menuController.close('app-menu');

    this.businessService.setCurrentBusiness(targetBusinessId);

    const SWITCH_TIMEOUT_MS: number = 10000;

    const businessSubscription: Subscription = race(
      this.businessService.business$.pipe(
        filter(
          (business): business is Business =>
            business !== null && String(business.id) === targetBusinessId
        ),
        take(1)
      ),
      timer(SWITCH_TIMEOUT_MS).pipe(map((): null => null))
    )
      .pipe(take(1))
      .subscribe({
        next: (business) => {
          this.isSwitchingToBusiness = false;
          if (business) {
            const lastPage: { url: string; state: StateType } | null =
              this.tabService.getLastPage('business');
            this.navController.navigateRoot(lastPage?.url || '/business/business-info', {
              state: lastPage?.state || {},
            });
          }
        },
        error: () => {
          this.isSwitchingToBusiness = false;
        },
      });

    this.subscriptions.add(businessSubscription);
  }

  async switchToCharity(): Promise<void> {
    if (this.isSwitchingToCharity) {
      return;
    }

    if (!this.permittedCharities || this.permittedCharities.length === 0) {
      return;
    }

    await this.performCharitySwitch();
  }

  private async performCharitySwitch(): Promise<void> {
    if (this.permittedCharities.length > 1) {
      const modal: HTMLIonModalElement = await this.modalController.create({
        component: CharitySelectorBottomSheetComponent,
        cssClass: 'bottom-sheet-modal',
        componentProps: { 
          permittedCharities: this.permittedCharities,
          currentCharityId: sessionStorage.getItem('currentCharityId') || sessionStorage.getItem('charityId')
        },
        backdropDismiss: true,
      });
      
      modal.onDidDismiss().then((result) => {
        if (result.data?.selected) {
          this.menuController.close('app-menu');
        } 
      });
      
      await modal.present();
      return;
    }

    this.isSwitchingToCharity = true;
    const targetCharityId: string = this.permittedCharities[0].id ?? '';

    this.menuController.close('app-menu');

    this.charityService.setCurrentCharity(targetCharityId);

    const SWITCH_TIMEOUT_MS: number = 10000;

    const charitySubscription: Subscription = race(
      this.charityService.charity$.pipe(
        filter(
          (charity): charity is Charity =>
            charity !== null && charity.id === targetCharityId
        ),
        take(1)
      ),
      timer(SWITCH_TIMEOUT_MS).pipe(map((): null => null))
    )
      .pipe(take(1))
      .subscribe({
        next: (charity) => {
          this.isSwitchingToCharity = false;
          if (charity) {
            const lastPage: { url: string; state: StateType } | null =
              this.tabService.getLastPage('charity');
            this.navController.navigateRoot(lastPage?.url || '/charity/charity-info', {
              state: lastPage?.state || {},
            });
          }
        },
        error: () => {
          this.isSwitchingToCharity = false;
        },
      });

    this.subscriptions.add(charitySubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
